"use client";
import axios from "axios";
import { Row } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, MoreHorizontal, Upload, Pencil, Trash } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDownloadProjectMutation } from "@/redux/slices/offline/offlineApiSlice";
import { OfflineProjectService } from "@/services/OfflineProjectService";
import { showSuccessToast, showErrorToast, showInfoToast } from "@/components/toast/toast";
import { useState } from "react";
import { UploadModal } from "@/components/table/components/upload-modal";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { db } from "@/services/Database";
import SecureStorage from "@/services/SecureStorage";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Loader2 } from "lucide-react";
import oktaService from "@/services/OktaService";
import DownloadLoader from "@/components/DownloadLoader";
import { backendApiUrl } from "@/lib/config";
interface ProjectData {
  id: string | number;
  status: string;
  [key: string]: any;
}

interface DataTableRowActionsProps<TData extends ProjectData> {
  row: Row<TData>;
  onDelete?: (project: TData) => void;
  canDelete?: boolean;
}

export function DataTableRowActions<TData extends ProjectData>({
  row,
  onDelete,
  canDelete = true,
}: DataTableRowActionsProps<TData>) {
  const navigate = useNavigate();
  const location = useLocation();
  const [downloadProject] = useDownloadProjectMutation();
  const isOffline = location.pathname.includes('offline');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const isOnline = useSelector((state: RootState) => state.netWork.isOnline)
  const isOfflineMode = useSelector((state: RootState) => state.offline.isOfflineMode)
  const isOfflineProject = isOffline || Boolean(row.original.offlineMetadata?.onlineId);
  const canUpload = isOfflineProject && !isOfflineMode && isOnline;
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownload = async () => {
    try {
      // Check if project already exists in offline database
      const existingProject = await db.projects.get(Number(row.original.id));

      if (existingProject) {
        setShowOverrideDialog(true);
        return;
      }

      await proceedWithDownload();
    } catch (error) {
      console.error('Error checking for existing project:', error);
      showErrorToast("Failed to check for existing project");
    }
  };

  const proceedWithDownload = async () => {
    try {
      setIsDownloading(true);
      const oktaUser = await oktaService.getUser();
      if (oktaUser.access_token) {
        const validResponse = await axios.get(
          `${backendApiUrl}/projects/validatePalletConfig/${row.original.id}`,
          {
            withCredentials: true,
            headers: {
              "authorization": `Bearer ${oktaUser.access_token}`
            }
          }
        );
        console.log(validResponse.data, 'validResponse.data')
        if (validResponse.data.isValid) {
          const response = await downloadProject(row.original.id).unwrap();
          console.log(response.data, 'project data')
          // // If we're overriding, delete the existing project first
          // const existingProject = await db.projects.get(Number(row.original.id));
          // if (existingProject) {
          //   await db.transaction('rw', [
          //     db.projects,
          //     db.newFailureModes,
          //     db.shippingModes,
          //     db.postShipTruckTestLayouts,
          //     db.pallets,
          //     db.primaryPackagingDetails,
          //     db.secondaryPackagingDetails,
          //     db.palletShippingModes,
          //     db.tertiaryPackagingDetails,
          //     db.palletDetails,
          //     db.unitLoadDetails,
          //     db.palletizingRecipes,
          //     db.palletLpnNumbers,
          //     db.leans,
          //     db.ftlRows,
          //     db.unitLoadPositions,
          //     db.unitLoadHeights,
          //     db.rqls,
          //     db.rqlDefects
          //   ], async () => {
          //     // Delete all related data
          //     await Promise.all([
          //       db.projects.delete(existingProject.id),
          //       db.newFailureModes.where('projectId').equals(existingProject.id).delete(),
          //       db.shippingModes.where('projectId').equals(existingProject.id).delete(),
          //       db.pallets.where('projectId').equals(existingProject.id).delete(),
          //       // Other tables will be cascade deleted due to foreign key relationships
          //     ]);
          //   });
          // }

          await OfflineProjectService.saveProjectOffline(response.data);
          setShowOverrideDialog(false);
          showSuccessToast("Project downloaded successfully");
          navigate(`/offline/projects/edit-project/${row.original.id}`);
        } else {
          console.log('errrrorrrr')
          showErrorToast("Please fill all necessary fields till LPN number stage to download project");
        }
      } else {
        showErrorToast("Failed to download project");
      }

    } catch (error) {
      console.error('Error downloading project:', error);
      showErrorToast("Failed to download project");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {isDownloading && <DownloadLoader />}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[160px]">
          <DropdownMenuItem
            onClick={() => navigate(
              isOfflineProject
                ? `/offline/projects/edit-project/${row.original.id}`
                : `/projects/edit-project/${row.original.id}`
            )}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>

          {isOfflineProject ? (
            canUpload ? (
              <DropdownMenuItem onClick={() => setIsUploadModalOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Upload to Online
              </DropdownMenuItem>
            ) : null
          ) : (
            <DropdownMenuItem onClick={handleDownload}>
              {isDownloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Download Offline
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="text-red-600 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
            disabled={!onDelete || !canDelete}
            onClick={() => onDelete?.(row.original)}
          >
            <Trash className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {
        isUploadModalOpen && (
          <UploadModal
            isOpen={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            project={row.original}
          />
        )
      }

      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Project Already Exists</DialogTitle>
            <DialogDescription>
              This project already exists in your offline storage. Would you like to override it with a fresh download?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverrideDialog(false)}>
              Cancel
            </Button>
            <Button className=" bg-blue" disabled={isDownloading} onClick={proceedWithDownload}>
              Override and Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
